// Shared display maps for the leagues module (F6). The league format/status
// label + badge-variant maps were duplicated verbatim across leagues-list.tsx
// and league-detail.tsx; this is the single module-local source of truth so a
// label change (e.g. a renamed format) lands in one place. MODULE-LOCAL ONLY —
// these are not a cross-module shared kernel; other modules must not import them.
//
// Source of truth for the FORMAT values is LEAGUE_FORMATS in leaguesApi.ts; the
// raw enum token is never shown to operators (we always map to a human label).

import type { LeagueFormat, LeagueTier } from "@/libs/leaguesApi";

// League format enum -> human label.
export const LEAGUE_FORMAT_LABELS: Record<LeagueFormat, string> = {
  rotating_doubles: "Rotating doubles",
  fixed_doubles: "Fixed doubles",
  singles: "Singles",
};

// League status -> human label. active is brand/primary; archived is muted.
export const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  archived: "Archived",
};

// Season status enum (registration | active | completed | cancelled) — display
// only this slice. Labels are human-cased; the MUI views apply lifecycle color.
export const SEASON_STATUS_LABELS: Record<string, string> = {
  registration: "Registration",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Player tier enum -> human label (title-cased). A division's tier RANGE is
// rendered from these labels ("Silver – Gold"), never the raw enum token. Source
// of truth for the tier values is LEAGUE_TIERS in leaguesApi.ts.
export const TIER_LABELS: Record<LeagueTier, string> = {
  iron: "Iron",
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum",
  diamond: "Diamond",
  grandmaster: "Grandmaster",
};

// Division status enum (open | full | locked | completed — display only this
// slice; the create flow always yields "open"). Labels are human-cased; an
// unmapped server enum falls back to the raw value so it is never silently dropped.
export const DIVISION_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  full: "Full",
  locked: "Locked",
  completed: "Completed",
};

// Registration status enum (registered | withdrawn | waitlisted — display only).
// Human-cased labels; the MUI views apply lifecycle color.
export const REGISTRATION_STATUS_LABELS: Record<string, string> = {
  registered: "Registered",
  withdrawn: "Withdrawn",
  waitlisted: "Waitlisted",
};

// Session status enum (scheduled | in_progress | completed | cancelled —
// display only this slice). Human-cased labels; an unmapped server enum falls
// back to the raw value so it is never silently dropped.
export const SESSION_STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Match status enum (scheduled | completed | cancelled — display only this
// slice; the create flow always yields "scheduled" and a reported result flips
// to "completed"). Human-cased labels; an unmapped server enum falls back to the
// raw value so it is never silently dropped.
export const MATCH_STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};

// A division's tier RANGE as a human string: both bounds -> "Silver – Gold";
// one bound -> only that bound ("Platinum"); neither -> "Any". Never emits a raw
// enum token; an unknown tier value falls back to title-casing its own token so
// it's still readable rather than dropped.
function tierLabel(tier: string | null | undefined): string | null {
  if (!tier) return null;
  return (
    TIER_LABELS[tier as LeagueTier] ??
    tier.charAt(0).toUpperCase() + tier.slice(1)
  );
}

export function tierRangeLabel(
  minTier: string | null | undefined,
  maxTier: string | null | undefined,
): string {
  const min = tierLabel(minTier);
  const max = tierLabel(maxTier);
  if (min && max) return `${min} – ${max}`;
  if (min) return min;
  if (max) return max;
  return "Any";
}
